import { queryOptions, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'bff/ApiClientProvider';
import {
  LookupsGetAllFundsForIntermediariesRequest,
  LookupsGetAllFundsRequest,
  LookupsGetBankBranchCodesAndNamesRequest,
  LookupsGetSigningLookupsRequest,
  LookupTypeResponseModel,
  ResultOfListOfLookupTypeResponseModel
} from 'bff/client';
import { clientService } from 'stores/client-store/client-store';
import { assertIsDefined } from '@sanlam-glacier/app-common/utils';

const lookupPrefix = 'lookup';

const allFundsQueryKeys = {
  base: () => ['get-all-funds'] as const,
  allFunds: (id: number) => [...allFundsQueryKeys.base(), id] as const,
  allFundsForIntermediary: (id: number) => [...allFundsQueryKeys.allFunds(id), id] as const,
  vatRate: () => [...allFundsQueryKeys.base(), 'vat-rate'] as const
};

export const getPartyTypeLookupOptions = () => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, 'GetPartyTypeLookup'],
    queryFn: () => lookupsApi.lookupsGetOneFromCache({ lookupTypeKey: 'IAPPARTYTYPE' }), // TODO: Get this string value from the BFF
    staleTime: Infinity,
    select: (resp) => {
      assertIsDefined(resp.data);

      return resp.data.map(({ value, description }) => ({
        clientTypeValue: value,
        clientTypeDescription: description
      }));
    }
  });
};

export const getLookupOptions = (lookupTypeKey: string) => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, lookupTypeKey],
    queryFn: () => lookupsApi.lookupsGetOneFromCache({ lookupTypeKey }),
    staleTime: Infinity,
    select: (response) => {
      assertIsDefined(response.data);

      const output: Record<string, { description: string; extendedDescription: string }> =
        response.data.reduce((acc, current) => {
          return {
            ...acc,
            [current.value]: {
              description: current.description,
              extendedDescription: current.extendedDescription
            }
          };
        }, {});

      return output;
    }
  });
};

export const getDropdownLookupOptions = (lookupTypeKey: string, order: boolean = false) => {
  return {
    ...getLookupOptions(lookupTypeKey),
    select: (response: ResultOfListOfLookupTypeResponseModel) => {
      assertIsDefined(response.data);
      const mappedLookup = response.data.map(({ value, description }: LookupTypeResponseModel) => ({
        value,
        content: description
      }));
      if (order)
        return mappedLookup.sort((a, b) => {
          if (a.content < b.content) return -1;
          if (a.content > b.content) return 1;
          return 0;
        });
      return mappedLookup;
    }
  };
};

export const getAllFundsOptions = (getAllFundsRequest: LookupsGetAllFundsRequest) => {
  const { lookupsApi } = useApiClient();

  return queryOptions({
    queryKey: allFundsQueryKeys.allFunds(getAllFundsRequest.productId!),
    queryFn: () => lookupsApi.lookupsGetAllFunds(getAllFundsRequest),
    select: (response) => response.data!
  });
};

export const useInvalidateFundsForIntermediaries = (productId: number) => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: allFundsQueryKeys.allFundsForIntermediary(productId!),
      refetchType: 'all'
    });
};

export const getAllFundsForIntermediariesOptions = (
  getAllFundsForIntermediariesRequest: LookupsGetAllFundsForIntermediariesRequest
) => {
  const { lookupsApi } = useApiClient();
  return queryOptions({
    queryKey: allFundsQueryKeys.allFundsForIntermediary(
      getAllFundsForIntermediariesRequest.productId!
    ),
    refetchOnWindowFocus: false,
    queryFn: () =>
      lookupsApi.lookupsGetAllFundsForIntermediaries(getAllFundsForIntermediariesRequest),
    select: (response) => response.data!
  });
};

export const getVatRateOptions = () => {
  const { lookupsApi } = useApiClient();

  return queryOptions({
    queryKey: allFundsQueryKeys.vatRate(),
    queryFn: () => lookupsApi.lookupsGetVatRate(),
    select: (response) => response.data!
  });
};

export const getSigningOptions = (getSigningOptionsRequest: LookupsGetSigningLookupsRequest) => {
  const { lookupsApi } = useApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, 'signing', getSigningOptionsRequest.partyType],
    queryFn: () => lookupsApi.lookupsGetSigningLookups(getSigningOptionsRequest),
    select: (response) => response.data!
  });
};

export const getBankNamesOptions = () => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, 'GetBankNames'],
    queryFn: () => lookupsApi.lookupsGetBankNames(),
    staleTime: Infinity,
    select: (resp) => {
      assertIsDefined(resp.data);

      return resp.data!;
    }
  });
};

export const getBankAccountTypesOptions = () => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, 'GetBankAccountTypes'],
    queryFn: () => lookupsApi.lookupsGetBankAccountTypes(),
    staleTime: Infinity,
    select: (resp) => {
      assertIsDefined(resp.data);

      return resp.data!;
    }
  });
};

export const getBankBranchCodesAndNamesOptions = (
  request: LookupsGetBankBranchCodesAndNamesRequest
) => {
  const { lookupsApi } = clientService.getApiClient();
  return queryOptions({
    queryKey: [lookupPrefix, 'GetBankBranchCodesAndNames', request.bankNameId ?? undefined],
    queryFn: () => lookupsApi.lookupsGetBankBranchCodesAndNames(request),
    staleTime: Infinity,
    select: (resp) => {
      assertIsDefined(resp.data);

      return resp.data!;
    }
  });
};

export const getProviderNameOptions = () => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [lookupPrefix, 'GetProviderNames'],
    queryFn: () => lookupsApi.lookupsGetProviderNames(),

    staleTime: Infinity,
    select: (resp) => {
      assertIsDefined(resp.data);

      return resp.data!;
    }
  });
};

// export const getInvestmentNamesOptions = () => {
//   const { lookupsApi } = clientService.getApiClient();

//   return queryOptions({
//     queryKey: [lookupPrefix, 'GetInvestmentNames'],
//     queryFn: () => lookupsApi.lookupsGetInvestmentNames(),

//     staleTime: Infinity,
//     select: (resp) => {
//       assertIsDefined(resp.data);

//       return resp.data!;
//     }
//   });
// };
